import { FC, useEffect } from 'react';
import { useTranslation } from 'next-i18next';

import { clientConfig } from '@/config/clientConfig';
import { useBeneficiary } from '@/entities/beneficiary';
import { InputCopy } from '@/shared/components';
import { useRegenerateBeneficiaryInviteMutation } from '@/shared/generated/graphql';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { Textarea, TextareaProps } from '@/shared/uikitV2/Textarea';

type GenerateRegistrationLinkProps = {
  onLinkGenerated?: (link: string) => void;
  textareaProps?: TextareaProps;
} & ({ alias: 'assistant'; itemId: string } | { alias: 'nextOwner'; itemId: number });

export const GenerateRegistrationLink: FC<GenerateRegistrationLinkProps> = ({
  alias = 'assistant',
  itemId,
  onLinkGenerated,
  textareaProps,
}) => {
  const { t } = useTranslation('common');
  const { getBeneficiary } = useBeneficiary();
  const sjDb = useSJDatabase();
  const [regenerateNextOwnerLink] = useRegenerateBeneficiaryInviteMutation({
    variables: { beneficiaryProfileId: itemId as number },
  });

  const selfUrl = clientConfig.selfUrl;

  const regenerate = async () => {
    if (alias === 'nextOwner') {
      const nextOwner = sjDb.collections.beneficiaries?.findOne(
        (no) => no.centralDbProfileId === itemId,
      );

      if (nextOwner) {
        try {
          const newLink = await regenerateNextOwnerLink();
          if (newLink.data?.regenerateBeneficiaryInvite.invitationCode) {
            sjDb.collections.beneficiaries?.update({
              ...nextOwner,
              invitationCode: newLink.data.regenerateBeneficiaryInvite.invitationCode,
            });
          }
        } catch (e) {
          console.warn('ERROR while regenerate new registration link: ', e);
        }
      }
    }
  };

  useEffect(() => {
    const nextOwner = alias === 'nextOwner' ? getBeneficiary(itemId) : undefined;
    if (nextOwner && !nextOwner.invitationCode) {
      regenerate();
    }
  }, [getBeneficiary, alias, itemId]);

  const nextOwnerLink = () => {
    return `${selfUrl}?beneficiarydata=${getBeneficiary(itemId)?.invitationCode}`;
  };

  const assistantLink = `${selfUrl}/auth/login/assistant?delegationRequestId=${itemId}`;

  const generatedText = alias === 'assistant' ? assistantLink : nextOwnerLink();

  useEffect(() => {
    if (generatedText) {
      onLinkGenerated?.(generatedText);
    }
  }, [generatedText]);

  return textareaProps ? (
    <Textarea value={generatedText} {...textareaProps} />
  ) : (
    // redesign InputCopy or remove and use only Textarea
    <InputCopy value={generatedText} copiedText={t('common:linkCopied')} />
  );
};
